import * as DataTableTypes from './DataTableTypes';
import {
  DATA_TABLE_PAGINATOR_HEIGHT,
  DATA_TABLE_PAGINATOR_HEIGHT_PX,
  DATA_TABLE_QUICK_REPORTS_HEIGHT,
  DATA_TABLE_QUICK_REPORTS_HEIGHT_PX,
  DATA_TABLE_ROW_HEIGHT,
  DATA_TABLE_ROW_HEIGHT_LARGE,
  DATA_TABLE_ROW_HEIGHT_PX,
  DATA_TABLE_ROW_HEIGHT_PX_LARGE,
  DATA_TABLE_ROW_HEIGHT_PX_SMALL,
  DATA_TABLE_ROW_HEIGHT_SMALL,
  DATA_TABLE_SECONDARY_TOOLBAR_HEIGHT_PX,
  DATA_TABLE_TOOLBAR_HEIGHT,
  DATA_TABLE_TOOLBAR_HEIGHT_PX
} from './DataTableHeights';

export const getDataTableHeight = (
  hasPagination: boolean,
  hasToolbar: boolean,
  hasSecondaryToolbar: boolean,
  isFetching: boolean,
  hasQuickReports: boolean
): string => {
  // Assuming during fetching that we can have pagination
  const paginatorHeight = hasPagination || isFetching ? DATA_TABLE_PAGINATOR_HEIGHT_PX : '0px';
  const quickReportsHeight = hasQuickReports ? DATA_TABLE_QUICK_REPORTS_HEIGHT_PX : '0px';
  // Assuming during fetching that we can have toolbar
  const toolbarHeight = hasToolbar || isFetching ? DATA_TABLE_TOOLBAR_HEIGHT_PX : '0px';
  const secondaryToolbarHeight = hasSecondaryToolbar || isFetching ? DATA_TABLE_SECONDARY_TOOLBAR_HEIGHT_PX : '0px';
  return `calc(100% - ${paginatorHeight} - ${toolbarHeight} - ${quickReportsHeight} - ${secondaryToolbarHeight})`;
};

interface CalculateTableHeight {
  tableHeight: string;
  options: {
    rowHeight?: DataTableTypes.DataTableRowHeight;
    pageIndex?: number;
    dataLength: number;
    hasPagination: boolean;
    hasToolbar: boolean;
    isFetching?: boolean;
    limit: number;
    isQuickReportsEnabled?: boolean;
  };
}

export const getRowHeightWithPx = (rowHeight?: DataTableTypes.DataTableRowHeight, $isSkeleton?: boolean) => {
  if (!rowHeight || rowHeight === 'medium') return DATA_TABLE_ROW_HEIGHT_PX;
  if (rowHeight === 'small') return DATA_TABLE_ROW_HEIGHT_PX_SMALL;
  if (rowHeight === 'large') return DATA_TABLE_ROW_HEIGHT_PX_LARGE;
  if (rowHeight === 'auto') return $isSkeleton ? DATA_TABLE_ROW_HEIGHT_PX : 'auto';
  return `${rowHeight}px`;
};

export const getRowHeight = (rowHeight?: DataTableTypes.DataTableRowHeight) => {
  if (!rowHeight || rowHeight === 'medium') return DATA_TABLE_ROW_HEIGHT;
  if (rowHeight === 'small') return DATA_TABLE_ROW_HEIGHT_SMALL;
  if (rowHeight === 'large') return DATA_TABLE_ROW_HEIGHT_LARGE;
  if (rowHeight === 'auto') return 'auto';
  return rowHeight;
};

export const calculateTableWrapperHeight = ({ tableHeight, options }: CalculateTableHeight): string => {
  const { dataLength, hasPagination, hasToolbar, isFetching, limit, isQuickReportsEnabled, rowHeight } = options;

  // Make height calculation only for height based on content
  if (tableHeight !== 'content') return tableHeight;
  if (rowHeight === 'auto') return '100%';

  // Display EmptyResults if no data
  if (!isFetching && dataLength === 0) return '500px';

  const toolbarHeight = hasToolbar ? DATA_TABLE_TOOLBAR_HEIGHT : 0;
  const quickReportsHeight = isQuickReportsEnabled ? DATA_TABLE_QUICK_REPORTS_HEIGHT : 0;
  const paginatorHeight = hasPagination || isFetching ? DATA_TABLE_PAGINATOR_HEIGHT : 0;
  const THEAD_ELEMENT = 1;
  // TODO: Verify why we need this adjustment. Probably calculation logic should be better
  const MANUAL_HEIGHT_ADJUSTMENT = 20;
  const rowH = getRowHeight(rowHeight);
  const calculatedRowHeight = typeof rowH === 'number' ? rowH : 1;
  // During fetching calculate table height based on limit
  const dataHeight = ((isFetching ? limit : dataLength) + THEAD_ELEMENT) * calculatedRowHeight;
  const calculatedHeight = dataHeight + toolbarHeight + paginatorHeight + MANUAL_HEIGHT_ADJUSTMENT + quickReportsHeight;
  return `${calculatedHeight}px`;
};
